import {Tooltip} from 'platform/components';
import {Icon, Show} from 'platform/foundation';
import {css} from 'styled-components';

import {
  ForwardedRef,
  MutableRefObject,
  forwardRef,
  ForwardRefRenderFunction,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {FormError} from '../Field/constants/FormError';
import {FormHelper} from '../Field/constants/FormHelper';
import {FormLabel} from '../Field/constants/FormLabel';
import {Adornment} from './components/Adornment';
import {InputAdornment} from './components/InputAdornment';
import {TextFieldContextProvider} from './components/TextFieldContextProvider';
import {
  HelpIconWrapper,
  Input,
  InputLabel,
  InputWrapper,
  LabelWrapper,
  Wrapper,
  WrapperFilled,
  WrapperInline,
  WrapperReadonly,
} from './utils/styles';
import {MappedInputProps, TextFieldProps} from './utils/types';

type FocusStates = 'none' | 'default' | 'extended';
/**
 * @deprecated - use platform instead
 */
const TextFieldComponent: ForwardRefRenderFunction<HTMLInputElement, TextFieldProps> = (
  props,
  forwardedRef
) => {
  const {
    error,
    variant,
    helperText,
    label,
    endAdornment,
    counter,
    leaveOnEnter,
    helpIconText,
    tooltipText,
    className,
    style,
    autoComplete = 'off',
    ...inputAttributes
  } = props;

  const {onClick, onChange, onFocus, onBlur, onKeyDown, name, disabled, required} = inputAttributes;

  const inputRef = useRef<HTMLInputElement>(null);
  const [currentLength, setCurrentLength] = useState<number>();
  const [focusState, setFocusState] = useState<FocusStates>('none');

  const combinedRefs = useCombinedRefs(forwardedRef, inputRef);

  /**
   * Mainly for `styledMap`
   */
  const mappedProps = useMemo<MappedInputProps>(
    () => ({
      disabled,
      error,
      withEndAdornment: !!endAdornment,
      hasFocus: focusState !== 'none',
      defaultFocus: focusState === 'default',
      extendedFocus: focusState === 'extended',
    }),
    [disabled, error, endAdornment, focusState]
  );

  const HelperText = error ? FormError : FormHelper;
  const isInline = variant === 'inline';
  const showCounter = !isInline && counter?.show;
  const showLabelWrapper = Boolean(label) || showCounter;

  const showEndAdornment = !isInline && endAdornment;
  const endAdornmentList = Array.isArray(endAdornment) ? endAdornment : [endAdornment];

  const handleSetFocusState = useCallback(
    (newState: FocusStates) => {
      if (disabled) {
        setFocusState('none');
        return;
      }

      setFocusState(newState);
    },
    [disabled]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.currentTarget.blur();
      }

      onKeyDown?.(e);
    },
    [onKeyDown]
  );

  const focusInput = useCallback(() => {
    inputRef.current?.focus();
    handleSetFocusState('default');
  }, [handleSetFocusState]);

  const isFirefox = () => navigator.userAgent.includes('Firefox');

  const commonProps: TextFieldProps = {
    ...inputAttributes,
    autoComplete: autoComplete === 'off' ? (isFirefox() ? 'off' : 'disabled') : autoComplete, // eslint-disable-line no-nested-ternary -- Pre-existing violation
    className: 'text-field-input',
    onChange: (e) => {
      if (counter?.show) {
        setCurrentLength(e.target.value?.length);
      }

      onChange?.(e);
      if (focusState !== 'none') {
        handleSetFocusState('default');
      }
    },
    onClick: (e) => {
      onClick?.(e);
      handleSetFocusState('default');
    },
    onFocus: (e) => {
      onFocus?.(e);
      handleSetFocusState('extended');
    },
    onBlur: (e) => {
      onBlur?.(e);
      handleSetFocusState('none');
    },
    onKeyDown: leaveOnEnter ? handleKeyDown : onKeyDown,
  };

  const WrapperComponent = useMemo(() => {
    if (variant === 'inline') {
      return WrapperInline;
    }

    if (variant === 'filled') {
      return WrapperFilled;
    }

    if (variant === 'readonly') {
      return WrapperReadonly;
    }

    // "default" variant
    return Wrapper;
  }, [variant]);

  const tooltipContent = useMemo(() => {
    if (isNotNilOrEmpty(tooltipText)) {
      return tooltipText;
    }
    if (isInline && isNotNilOrEmpty(helperText)) {
      return helperText;
    }
    return undefined;
  }, [tooltipText, helperText, isInline]);

  useEffect(
    function syncCurrentLength() {
      if (counter?.show) {
        setCurrentLength(inputRef.current?.value?.length);
      }
    },
    [counter?.show]
  );

  return (
    <TextFieldContextProvider focusInput={focusInput} disabled={disabled}>
      <WrapperComponent
        {...mappedProps}
        className={`form-field-wrapper ${className ?? ''}`}
        style={style}
        data-testid={`${`text-field-${name}`}-wrapper`}
      >
        <Show when={showLabelWrapper}>
          <LabelWrapper data-testid={`${`text-field-${name}`}-label-wrapper`}>
            <Show when={isNotNilOrEmpty(label)}>
              <InputLabel data-testid={`${`text-field-${name}`}-label`}>
                {required && '* '}
                {label}
                <Show when={isNotNilOrEmpty(helpIconText)}>
                  <Tooltip placement="top" label={helpIconText}>
                    <HelpIconWrapper
                      css={css`
                        margin-left: 4px;
                      `}
                      inherit
                    >
                      <Icon value="action/help" />
                    </HelpIconWrapper>
                  </Tooltip>
                </Show>
              </InputLabel>
            </Show>
            <Show when={showCounter}>
              <FormLabel data-testid={`${`text-field-${name}`}-label-counter`}>
                {currentLength}/{counter?.max}
              </FormLabel>
            </Show>
          </LabelWrapper>
        </Show>

        <Tooltip placement="top" label={tooltipContent} isDisabled={isNilOrEmpty(tooltipContent)}>
          <InputWrapper
            data-testid={`${`text-field-${name}`}-input-wrapper`}
            {...mappedProps}
            className="text-field-input-wrapper"
          >
            <Input
              data-testid={`${`text-field-${name}`}-input`}
              {...commonProps}
              ref={combinedRefs}
            />

            <Show when={error && !isInline}>
              <InputAdornment error classNameSuffix="end" Icon={<Icon value="alert/warning" />} />
            </Show>

            {showEndAdornment &&
              endAdornmentList.map((endAd, i) => (
                <Adornment inputName={name} key={endAd?.key ?? `endAdornment-${i}`} {...endAd} />
              ))}
          </InputWrapper>
        </Tooltip>

        <Show when={isNotNilOrEmpty(helperText) && !isInline}>
          <HelperText data-testid={`text-field-${name}-helper-text-wrapper`}>
            {helperText}
          </HelperText>
        </Show>
      </WrapperComponent>
    </TextFieldContextProvider>
  );
};

const useCombinedRefs = <T,>(
  ...refs: (ForwardedRef<T> | MutableRefObject<T | undefined>)[]
): MutableRefObject<T | null> => {
  const targetRef = useRef<T>(null);

  useEffect(
    function syncForwardedRefs() {
      refs.forEach((ref) => {
        if (!ref) {
          return;
        }

        if (typeof ref === 'function') {
          ref(targetRef.current ?? null);
        } else {
          ref.current = targetRef.current ?? null;
        }
      });
    },
    [refs]
  );

  return targetRef;
};

export const TextField = forwardRef(TextFieldComponent);
